package org.teddapp.project.ui.pomodoro

import androidx.compose.runtime.ReadOnlyComposable

import androidx.compose.runtime.Composable

import androidx.compose.ui.graphics.Color
import com.tedd.db.Milestone
import com.tedd.db.Objective
import com.tedd.db.Organization
import com.tedd.db.Pomodoro_item
import com.tedd.db.Project
import com.tedd.db.Task
import org.teddapp.project.ui.TeddTokens

/** Date du jour ISO — délégué au clock central. */
internal val POM_TODAY_ISO: String
    get() = org.teddapp.project.db.TODAY_ISO

/** Durées de minuteur présélectionnées (spec POMODORO §7.1). */
internal val POM_PRESET_DURATIONS: List<Int> = listOf(5, 15, 25, 45, 60, 90)

/** Priorités (spec §3.3). Valeur DB = ordre de tri. */
internal enum class PomPriority(val value: Long, val label: String, val icon: String) {
    Urgent(1L, "Urgente", "flame"),
    Critical(2L, "Critique", "spark"),
    High(3L, "Haute", "arrow-up"),
    Medium(4L, "Moyenne", "more"),
    Low(5L, "Faible", "arrow-down");

    /**
     * Couleur du repère de priorité. Elle n'est jamais seule : l'icône et le libellé
     * disent la même chose.
     */
    val color: Color
        @Composable @ReadOnlyComposable
        get() = when (this) {
            Urgent -> TeddTokens.Danger
            Critical -> TeddTokens.Warning
            High -> TeddTokens.ModFocus
            Medium -> TeddTokens.InkMuted
            Low -> TeddTokens.InkSubtle
        }

    companion object {
        fun fromValue(v: Long): PomPriority = entries.firstOrNull { it.value == v } ?: Medium
    }
}

/**
 * Résultat de la résolution du titre d'un item (spec §8.14).
 * `title` = "Sans titre" si la source est introuvable.
 */
/** [minutes] : le temps prévu, celui de la tâche derrière l'item ou celui saisi sur une tâche rapide. */
internal data class ResolvedTitle(val title: String, val context: String, val minutes: Int? = null)

/**
 * Résout le titre + contexte d'un item Pomodoro en fonction de sa source.
 *
 * Pour "action" (tâche d'objectif) : titre = task.title, contexte = "jalon · objectif"
 * Pour "project_task" : titre = task.title, contexte = "projet · organisation"
 * Pour "quick" : titre = custom_title, contexte = "Tâche rapide · Pomodoro"
 * Pour "exercise" / "exercise_task" : placeholder simple
 * Si source introuvable : "Sans titre" + contexte vide.
 */
internal fun resolveItemTitle(
    item: Pomodoro_item,
    tasksById: Map<String, Task>,
    milestonesById: Map<String, Milestone>,
    objectivesById: Map<String, Objective>,
    projectsById: Map<String, Project>,
    organizationsById: Map<String, Organization>,
): ResolvedTitle = when (item.source_type) {
    "quick" -> ResolvedTitle(
        title = item.custom_title ?: "Tâche rapide",
        context = "Tâche rapide · Pomodoro",
        minutes = item.estimated_minutes?.toInt(),
    )
    "action" -> {
        val task = tasksById[item.source_id]
        if (task == null) {
            ResolvedTitle("Sans titre", "")
        } else {
            val milestone = task.parent_id?.let { milestonesById[it] }
            val objective = milestone?.objective_id?.let { objectivesById[it] }
            val ctx = listOfNotNull(milestone?.title, objective?.title).joinToString(" · ")
            ResolvedTitle(task.title, ctx, task.estimated_minutes?.toInt())
        }
    }
    "project_task" -> {
        val task = tasksById[item.source_id]
        if (task == null) {
            ResolvedTitle("Sans titre", "")
        } else {
            val project = task.parent_id?.let { projectsById[it] }
            val org = project?.organization_id?.let { organizationsById[it] }
            val ctx = listOfNotNull(project?.name, org?.name).joinToString(" · ")
            ResolvedTitle(task.title, ctx, task.estimated_minutes?.toInt())
        }
    }
    "exercise" -> ResolvedTitle(item.custom_title ?: "Exercice", "Challenge · Exercice")
    "exercise_task" -> ResolvedTitle(item.custom_title ?: "Tâche exercice", "Tâche · Exercice")
    else -> ResolvedTitle("Sans titre", "")
}

/** Format MM:SS pour l'affichage du minuteur. */
internal fun formatMmSs(remainingSec: Int): String {
    val sec = remainingSec.coerceAtLeast(0)
    val m = sec / 60
    val s = sec % 60
    return "${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}"
}

/** Ajoute n jours à une date ISO YYYY-MM-DD. Approche naïve sans kotlinx-datetime. */
internal fun isoAddDays(iso: String, days: Int): String {
    if (days == 0) return iso
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    var y = parts[0].toIntOrNull() ?: return iso
    var m = parts[1].toIntOrNull() ?: return iso
    var d = parts[2].toIntOrNull() ?: return iso
    var remaining = days
    while (remaining > 0) {
        d += 1
        if (d > daysInMonth(y, m)) {
            d = 1
            m += 1
            if (m > 12) { m = 1; y += 1 }
        }
        remaining -= 1
    }
    while (remaining < 0) {
        d -= 1
        if (d < 1) {
            m -= 1
            if (m < 1) { m = 12; y -= 1 }
            d = daysInMonth(y, m)
        }
        remaining += 1
    }
    return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
}

private fun daysInMonth(year: Int, month: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if ((year % 4 == 0 && year % 100 != 0) || year % 400 == 0) 29 else 28
    else -> 30
}

/** Sous-titre court pour un compteur "N/M tâches" + barre. */
internal data class ProgressStats(val total: Int, val done: Int) {
    val percent: Int get() = if (total == 0) 0 else (done * 100 / total)
}

internal fun statsFromItems(items: List<Pomodoro_item>): ProgressStats =
    ProgressStats(total = items.size, done = items.count { it.completed != 0L })

